import React, { useCallback } from "react";
import { Text, View } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { capitalize } from "./capitalize";
import { handleClick } from "lib/utils/navigator";

export const TemplateSevenProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  return (
    <View
      style={{
        ...styles.flexRow,
        gap: spacing["3"], // Reduced from spacing[4] for better density
        marginTop: spacing["3"], // Reduced from spacing[5] for tighter layout
        ...style,
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            width: "20vw",
            textAlign: "right",
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing["1"], // Reduced from spacing[1.5] for tighter content
          width: "80vw",
          // borderLeft: "1px solid gray",
          // paddingLeft: spacing["4"],
        }}
      >
        {children}
      </View>
    </View>
  );
};

export const TemplateSevenResumeSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
    <div
    onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: spacing["1"], // Dramatically reduced from spacing[4] (12pt) to spacing[1] (3pt)
        marginTop: spacing["3"], // Dramatically reduced from spacing[10] (30pt) to spacing[3] (9pt)
        // ...style,
        cursor:"pointer"
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            padding: spacing["1.5"],
            width: "100%",
            textAlign: "center",
            backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
          }}
        >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
            {capitalize(heading)}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing["1"], // Reduced from spacing[2] (6pt) to spacing[1] (3pt) for tighter content
        
        }}
      >
        {children}
      </View>
    </div>
  );
};

// Enhanced version with subsections for better orphan heading prevention
export const TemplateSevenResumeSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray[0];
  const restChildren = childrenArray.slice(1);
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            marginTop: spacing["3"],
          }}
          wrap={false}
        >
          {/* Heading */}
          <View
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"], // Consistent with main component
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`, // Maintain background styling
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </View>
          {/* First child inside the same container */}
          {firstChild}
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            marginTop: spacing["3"],
          }}
        >
          <View
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </View>
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            marginTop: spacing["0.5"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

// Compact text component for better space utilization
export const TemplateSevenPDFText = ({
  bold = false,
  themeColor,
  style = {},
  children,
}: {
  bold?: boolean;
  themeColor?: string;
  style?: Style;
  children: React.ReactNode;
}) => (
    <Text
    style={{
      color: themeColor || DEFAULT_FONT_COLOR,
      fontWeight: bold ? "bold" : "normal",
      lineHeight: "1.2", // Tight line height
      ...style,
    }}
    debug={DEBUG_RESUME_PDF_FLAG}
  >
    {children}
  </Text>
);